/**
 * Referral Program Component
 * 
 * Viral loop system with:
 * - Unique referral codes
 * - Social sharing
 * - Reward tracking
 * - Leaderboard
 */

import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Share2,
  Copy,
  Check,
  Gift,
  Users,
  Trophy,
  Sparkles,
  Twitter,
  Facebook,
  MessageCircle,
  Mail,
  Linkedin,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { getBaseUrl, getReferralLink } from '../config/app-config';
import { copyToClipboard } from '../utils/clipboard';

interface ReferralStats {
  referralCode: string;
  totalReferred: number;
  converted: number;
  pending: number;
  rewardsEarned: number;
}

interface Referrer {
  name: string;
  referrals: number;
  rank: number;
}

export function ReferralProgram() {
  const [stats, setStats] = useState<ReferralStats>({
    referralCode: '',
    totalReferred: 0,
    converted: 0,
    pending: 0,
    rewardsEarned: 0,
  });
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [leaderboard, setLeaderboard] = useState<Referrer[]>([]);

  useEffect(() => {
    loadReferralStats();
    loadLeaderboard();
  }, []);

  const loadReferralStats = () => {
    // Get or create referral code
    let code = localStorage.getItem('tension_referral_code');
    if (!code) {
      code = generateReferralCode();
      localStorage.setItem('tension_referral_code', code);
    }

    // Load stats from localStorage (in production, this would be from backend)
    const referred = parseInt(localStorage.getItem('tension_total_referred') || '0');
    const converted = parseInt(localStorage.getItem('tension_converted') || '0');

    setStats({
      referralCode: code,
      totalReferred: referred,
      converted: converted,
      pending: referred - converted,
      rewardsEarned: converted * 7, // 7 days free per referral
    });
  };

  const loadLeaderboard = () => {
    // Mock leaderboard data
    setLeaderboard([
      { name: 'Sarah M.', referrals: 12, rank: 1 },
      { name: 'Alex C.', referrals: 9, rank: 2 },
      { name: 'Taylor J.', referrals: 8, rank: 3 },
      { name: 'Jordan K.', referrals: 6, rank: 4 },
      { name: 'You', referrals: stats.converted, rank: 5 },
    ].sort((a, b) => b.referrals - a.referrals)
     .map((r, i) => ({ ...r, rank: i + 1 })));
  };

  const generateReferralCode = (): string => {
    const user = JSON.parse(localStorage.getItem('tension_user') || '{}');
    const email = typeof user?.email === 'string' ? user.email : 'user';
    const username = email.split('@')[0].toLowerCase();
    const random = Math.random().toString(36).substring(2, 6);
    return `${username}_${random}`;
  };

  const getReferralURL = () => {
    return getReferralLink(stats.referralCode);
  };

  const handleCopy = async () => {
    await copyToClipboard(getReferralURL());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(
      "I'm using TENSION to track my relationship patterns and it's been game-changing. Join me and get 7 days of Premium free!"
    );
    const url = encodeURIComponent(getReferralURL());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareViaFacebook = () => {
    const url = encodeURIComponent(getReferralURL());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const shareViaLinkedIn = () => {
    const url = encodeURIComponent(getReferralURL());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Try TENSION - Get 7 Days Free!');
    const body = encodeURIComponent(
      `Hi!\n\nI've been using TENSION to understand my relationship patterns better, and I think you'd find it useful too.\n\nIt helps you measure relationship tension before it causes distance - with daily check-ins, RTI scoring, and personalized action cards.\n\nJoin using my link and get 7 days of Premium free:\n${getReferralURL()}\n\nLet me know what you think!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      `Check out TENSION - it helps track relationship patterns and reduce anxiety. Get 7 days free: ${getReferralURL()}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareOptions = [
    { name: 'Twitter', icon: Twitter, action: shareViaTwitter, color: 'bg-blue-500' },
    { name: 'Facebook', icon: Facebook, action: shareViaFacebook, color: 'bg-blue-600' },
    { name: 'LinkedIn', icon: Linkedin, action: shareViaLinkedIn, color: 'bg-blue-700' },
    { name: 'WhatsApp', icon: MessageCircle, action: shareViaWhatsApp, color: 'bg-green-500' },
    { name: 'Email', icon: Mail, action: shareViaEmail, color: 'bg-neutral-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full mb-4">
          <Gift className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Invite Friends, Get Rewards</h2>
        <p className="text-neutral-400 max-w-2xl mx-auto">
          Share TENSION with friends. For every person who joins and completes onboarding,
          you both get <span className="text-purple-400 font-semibold">7 days of Premium free</span>!
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 bg-gradient-to-br from-purple-900/30 to-purple-800/30 border-purple-700">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-purple-400" />
            <span className="text-sm text-neutral-400">Total Invited</span>
          </div>
          <p className="text-4xl font-bold text-white">{stats.totalReferred}</p>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-700">
          <div className="flex items-center gap-3 mb-2">
            <Check className="w-5 h-5 text-green-400" />
            <span className="text-sm text-neutral-400">Joined</span>
          </div>
          <p className="text-4xl font-bold text-white">{stats.converted}</p>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-pink-900/30 to-pink-800/30 border-pink-700">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-5 h-5 text-pink-400" />
            <span className="text-sm text-neutral-400">Days Earned</span>
          </div>
          <p className="text-4xl font-bold text-white">{stats.rewardsEarned}</p>
          <p className="text-xs text-neutral-400 mt-1">of Premium access</p>
        </Card>
      </div>

      {/* Referral Link */}
      <Card className="p-6 bg-neutral-800 border-neutral-700">
        <h3 className="text-lg font-semibold text-white mb-4">Your Referral Link</h3>
        
        <div className="flex gap-2">
          <Input
            value={getReferralURL()}
            readOnly
            className="bg-neutral-700 border-neutral-600 text-white"
          />
          <Button onClick={handleCopy} className="shrink-0">
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </>
            )}
          </Button>
        </div>

        <p className="text-sm text-neutral-400 mt-3">
          💡 <strong>Tip:</strong> Personalize your message when sharing! Explain how TENSION has helped you.
        </p>
      </Card>

      {/* Share Buttons */}
      <Card className="p-6 bg-neutral-800 border-neutral-700">
        <h3 className="text-lg font-semibold text-white mb-4">Share Directly</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {shareOptions.map((option) => (
            <button
              key={option.name}
              onClick={option.action}
              className={`${option.color} hover:opacity-90 text-white rounded-lg p-4 flex flex-col items-center gap-2 transition-opacity`}
            >
              <option.icon className="w-6 h-6" />
              <span className="text-sm font-medium">{option.name}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Rewards Tiers */}
      <Card className="p-6 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-700">
        <h3 className="text-lg font-semibold text-white mb-4">🎁 Referral Rewards</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
            <div>
              <p className="font-medium text-white">1-5 Referrals</p>
              <p className="text-sm text-neutral-400">Getting started</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-purple-400">7 days each</p>
              <p className="text-xs text-neutral-400">Premium</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
            <div>
              <p className="font-medium text-white">6-10 Referrals</p>
              <p className="text-sm text-neutral-400">Community builder</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-purple-400">1 month free</p>
              <p className="text-xs text-neutral-400">Premium</p>
            </div>
          </div>

          <div className="tension-glow flex items-center justify-between p-3 bg-gradient-to-r from-purple-900 to-pink-900 rounded-lg border border-purple-500">
            <div>
              <p className="font-medium text-white flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-400" />
                10+ Referrals
              </p>
              <p className="text-sm text-neutral-400">Growth champion</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-yellow-400">Pro for life</p>
              <p className="text-xs text-neutral-400">Forever free!</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Leaderboard */}
      <Card className="p-6 bg-neutral-800 border-neutral-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">🏆 Top Referrers</h3>
          <Trophy className="w-6 h-6 text-yellow-400" />
        </div>
        
        <div className="space-y-2">
          {leaderboard.map((referrer) => {
            const isUser = referrer.name === 'You';
            return (
              <div
                key={referrer.rank}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  isUser ? 'bg-purple-900/30 border border-purple-700' : 'bg-neutral-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                    referrer.rank === 1 ? 'bg-yellow-500 text-black' :
                    referrer.rank === 2 ? 'bg-gray-400 text-black' :
                    referrer.rank === 3 ? 'bg-amber-600 text-black' :
                    'bg-neutral-600 text-white'
                  }`}>
                    {referrer.rank}
                  </div>
                  <p className={`font-medium ${isUser ? 'text-purple-400' : 'text-white'}`}>
                    {referrer.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-white">{referrer.referrals}</p>
                  <p className="text-xs text-neutral-400">referrals</p>
                </div>
              </div>
            );
          })}
        </div>

        <Button 
          className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          onClick={() => toast.success('Keep sharing to climb the leaderboard!')}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Invite More Friends
        </Button>
      </Card>

      {/* Tips */}
      <Card className="p-6 bg-blue-900/20 border-blue-700">
        <h3 className="text-lg font-semibold text-blue-400 mb-3">💡 Referral Tips</h3>
        <ul className="space-y-2 text-sm text-neutral-300">
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span><strong>Be authentic:</strong> Share your personal experience with TENSION</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span><strong>Target your audience:</strong> Friends with anxious attachment or relationship anxiety</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span><strong>Share in communities:</strong> Post in r/attachment_theory, r/relationship_advice</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span><strong>Use stories:</strong> Share on Instagram/TikTok Stories with your referral link</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            <span><strong>Follow up:</strong> Check in with friends who signed up to help them get started</span>
          </li>
        </ul>
      </Card>
    </div>
  );
}
